import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ExternalLink, FileText } from 'lucide-react';

interface ContextArticleDisplayProps {
  context: string;
  contextUrl?: string;
  isNTBArticle?: boolean;
  className?: string;
}

const ContextArticleDisplay: React.FC<ContextArticleDisplayProps> = ({
  context,
  contextUrl,
  isNTBArticle = false,
  className = ""
}) => {
  const [showDialog, setShowDialog] = useState(false);

  const handleExternalUrl = () => {
    if (contextUrl && !isNTBArticle) {
      window.open(contextUrl, '_blank');
    }
  };

  const handleNTBArticleClick = () => {
    if (isNTBArticle) {
      setShowDialog(true);
    }
  };

  if (!context) {
    return null;
  }

  // Extract title from NTB content if it's an NTB article
  const getDisplayTitle = () => {
    if (isNTBArticle) {
      try {
        // Try to parse as structured NTB data first
        const ntbData = JSON.parse(context);
        if (ntbData.type === 'ntb_article' && ntbData.title) {
          return ntbData.title;
        }
      } catch {
        // Fallback: extract title from raw content
        const lines = context.split('\n').filter(line => line.trim());
        const firstLine = lines[0] || 'NTB Artikkel';
        // If first line looks like a title (reasonable length), use it
        return firstLine.length <= 200 ? firstLine : 'NTB Artikkel';
      }
    }
    // For regular context, show first 100 characters
    return context.length > 100 ? context.substring(0, 100) + '...' : context;
  };

  return (
    <div className={className}>
      <div className="border rounded-md p-4 bg-muted/50">
        <h4 className="text-sm font-medium text-muted-foreground mb-2">
          Kontekst
        </h4>
        
        <div className="flex items-start gap-2">
          {isNTBArticle ? (
            <FileText className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
          ) : (
            <ExternalLink className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
          )}
          
          <button
            onClick={isNTBArticle ? handleNTBArticleClick : handleExternalUrl}
            className="text-left text-sm text-primary hover:text-primary/80 hover:underline transition-colors flex-1"
          >
            {getDisplayTitle()}
          </button>
        </div>
      </div>

      {/* Dialog for NTB articles */}
      {isNTBArticle && (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {(() => {
                  try {
                    const ntbData = JSON.parse(context);
                    return ntbData.title || 'NTB Artikkel';
                  } catch {
                    return 'NTB Artikkel';
                  }
                })()}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {(() => {
                try {
                  const ntbData = JSON.parse(context);
                  if (ntbData.type === 'ntb_article') {
                    return (
                      <div className="space-y-4">
                        {ntbData.publishedDate && (
                          <div className="text-sm text-gray-600">
                            Publisert: {new Date(ntbData.publishedDate).toLocaleDateString('nb-NO')}
                          </div>
                        )}
                        
                        {ntbData.summary && (
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">Sammendrag:</h4>
                            <p className="text-gray-700 leading-relaxed">{ntbData.summary}</p>
                          </div>
                        )}
                        
                        <div className="prose max-w-none">
                          <div 
                            className="text-gray-800 leading-relaxed whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{ __html: ntbData.content || ntbData.summary || '' }}
                          />
                        </div>
                      </div>
                    );
                  }
                } catch {
                  // Fallback for legacy format
                }
                
                return (
                  <div 
                    className="prose max-w-none"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {context}
                  </div>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ContextArticleDisplay;